"use client"

import z from "zod";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { useState, useEffect } from "react";
import { CreateStatus } from "@/constants/status";
import { Button } from "../ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Dropdown, { dropdownCss, DropdownInterface } from "../ui/dropdown";
import GlobalCssStrings from "@/app/globalCssStrings";
import { getRequisitionserverfolder, postCreateRequisitionEntry } from "@/services/requisition.service";
import Loading from "../ui/loading";
// import { getRequisitionserverfolder } from "@/services/api"; // Adjust path as needed

interface TreeNode {
    name: string;
    path: string;
    children?: TreeNode[];
}

// Zod Schema for the form
export const RequisitionsFormSchema = z.object({
    domainID: z.string().min(1, "Please select a domain"),
    level1: z.string().min(1, "Please select a folder"),
    level2: z.string().optional(),
});

function buildNestedStructure(paths: string[]): TreeNode[] {
    const root: TreeNode[] = [];
    const map = new Map<string, TreeNode>();

    const sortedPaths = [...paths].sort((a, b) => {
        const depthA = (a.match(/\//g) || []).length;
        const depthB = (b.match(/\//g) || []).length;
        return depthA - depthB;
    });

    sortedPaths.forEach(path => {
        const parts = path.split('/').filter((p: string) => p);
        const name = parts[parts.length - 1];
        const parentPath = parts.length > 1 ? '/' + parts.slice(0, -1).join('/') : null;

        const node: TreeNode = {
            name: name,
            path: path,
            children: []
        };

        map.set(path, node);

        if (parentPath && map.has(parentPath)) {
            const parent = map.get(parentPath);
            if (parent) {
                parent.children!.push(node);
            }
        } else {
            root.push(node);
        }
    });

    return root;
}

interface Props {
    domains: DropdownInterface[],
    updateData: () => void,
}

export default function RequisitionsModal(props: Props) {
    const [open, setOpen] = useState(false);
    const [createStatus, setCreateStatus] = useState(CreateStatus.standBy);
    const [failedMessage, setFailedMessage] = useState("");
    const [nestedData, setNestedData] = useState<TreeNode[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const form = useForm<z.infer<typeof RequisitionsFormSchema>>({
        resolver: zodResolver(RequisitionsFormSchema),
        defaultValues: {
            domainID: "",
            level1: "",
            level2: "",
        },
    });

    const level1Value = form.watch('level1');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const paths = await getRequisitionserverfolder();
                const structured = buildNestedStructure(paths);
                setNestedData(structured);
            } catch (err) {
                setError((err as Error).message || 'Failed to load folders');
                console.error('Error fetching requisition folders:', err);
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            fetchData();
            // Auto-select first domain if available
            if (props.domains && props.domains.length > 0 && props.domains[0].value) {
                form.setValue('domainID', props.domains[0].value);
            }
        }
    }, [open]);

    // Reset level2 when level1 changes
    useEffect(() => {
        form.setValue('level2', '');
    }, [level1Value, form]);

    const getLevel2Options = (): TreeNode[] => {
        if (!level1Value) return [];
        const selected = nestedData.find(item => item.path === level1Value);
        return selected?.children || [];
    };

    async function CreateRequisitionEntry(data: z.infer<typeof RequisitionsFormSchema>) {
        setCreateStatus(CreateStatus.onGoing);
        
        // Use level2 if selected, otherwise use level1
        const pdfFolder = data.level2 || data.level1;

        const formData = new FormData();
        formData.append("domainID", data.domainID);
        formData.append("folders", pdfFolder);

        var result = null;
        try {
            result = await postCreateRequisitionEntry(formData);
        } catch (error) {
            setCreateStatus(CreateStatus.failed);
            errorHandler(error);
            return;
        }

        console.log(result?.message);
        if (result?.status == "Success") {
            setCreateStatus(CreateStatus.completed);
            props.updateData();
            form.reset();
            setTimeout(() => {
                setOpen(false);
                setCreateStatus(CreateStatus.standBy);
            }, 2000);
            return;
        }

        console.log(result?.message);
        setCreateStatus(CreateStatus.failed);
        setFailedMessage(result?.message || "Failed to create requisition entry");
        return;
    }

    function errorHandler(data: any) {
        console.log("Error occurred");
        console.log(data);
        setFailedMessage("An error occurred while creating the entry. Check console.log for more information");
    }

    const getSelectedPath = () => {
        const level2 = form.watch('level2');
        if (level2) return level2;
        if (level1Value) return level1Value;
        return '';
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">Create Requisition Entry</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Create Requisition Entry</DialogTitle>
                <DialogDescription> 
                </DialogDescription>
                <div>
                    {(createStatus === CreateStatus.standBy ||
                        createStatus === CreateStatus.notAuthroized) && (
                            <Form {...form}>
                                <form 
                                    autoComplete="on"
                                    className="grid"
                                    onSubmit={form.handleSubmit(CreateRequisitionEntry, errorHandler)}
                                >
                                    <div className="col-start-1 col-end-4 rounded-md border-black">
                                        <FormField
                                            control={form.control}
                                            name="domainID"
                                            render={({ field }) => (
                                                <FormItem className="mb-2">
                                                    <FormLabel>Domain</FormLabel>
                                                    <FormControl>
                                                        <Dropdown
                                                            {...field}
                                                            id="domainID"
                                                            title="domainID"
                                                            className={dropdownCss}
                                                        >
                                                            {props.domains}
                                                        </Dropdown>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        {loading && (
                                            <Loading text="Loading folders..." />
                                        )}

                                        {error && (
                                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-2">
                                                <p className="text-red-700 text-sm">{error}</p>
                                            </div>
                                        )}

                                        {!loading && !error && (
                                            <>
                                                {/* Level 1 Folder Dropdown */}
                                                <FormField
                                                    control={form.control}
                                                    name="level1"
                                                    render={({ field }) => (
                                                        <FormItem className="mb-2">
                                                            <FormLabel>Primary Folder *</FormLabel>
                                                            <FormControl>
                                                                <select
                                                                    {...field}
                                                                    className={GlobalCssStrings.InputCSS}
                                                                >
                                                                    <option value="">-- Select a folder --</option>
                                                                    {nestedData.map((option) => (
                                                                        <option key={option.path} value={option.path}>
                                                                            {option.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                {/* Level 2 Folder Dropdown */}
                                                {level1Value && getLevel2Options().length > 0 && (
                                                    <FormField
                                                        control={form.control}
                                                        name="level2"
                                                        render={({ field }) => (
                                                            <FormItem className="mb-2">
                                                                <FormLabel>Subfolder (Optional)</FormLabel>
                                                                <FormControl>
                                                                    <select
                                                                        {...field}
                                                                        className={GlobalCssStrings.InputCSS}
                                                                    >
                                                                        <option value="">-- Select a subfolder (optional) --</option>
                                                                        {getLevel2Options().map((option) => (
                                                                            <option key={option.path} value={option.path}>
                                                                                {option.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                )}

                                                {/* Selected Path Display */}
                                                {level1Value && (
                                                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                                        <p className="text-xs font-semibold text-blue-800 mb-1">
                                                            Selected Path:
                                                        </p>
                                                        <p className="text-blue-900 font-mono text-xs break-all">
                                                            {getSelectedPath()}
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div className="w-full">
                                        {createStatus == CreateStatus.notAuthroized && (
                                            <h1 className="w-full text-xl font-bold text-black dark:text-white mb-2 py-1 px-2 rounded-md bg-red-500">
                                                Authorization to create failed
                                            </h1>
                                        )}
                                        <Button
                                            className="w-full mt-4 cursor-pointer"
                                            type="submit"
                                            disabled={loading}
                                        >
                                            Create Entry
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                    )}
                </div>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            {createStatus == CreateStatus.onGoing && (
                                <div className="flex flex-col w-full max-w-2xl gap-y-4">
                                    <p>Creating Entry...</p>
                                </div>
                            )}
                            {createStatus == CreateStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl gap-y-4">
                                    <p>Created Entry! The modal will close momentarily</p>
                                </div>
                            )}
                            {createStatus == CreateStatus.failed && (
                                <div className="flex flex-col w-full max-w-2xl gap-y-4">
                                    <p>{failedMessage}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}